import {
  getOnboarding,
  type OnboardingState,
  toggleOnboarding,
  toggleOnboardingStep
} from "@prismicio/editor-fields"
import { useRefGetter } from "@prismicio/editor-support/React"
import { updateData, useRequest } from "@prismicio/editor-support/Suspense"
import { useToast } from "@prismicio/editor-ui"

import { env } from "../env"
import { getRepositoryName } from "../routing"

const baseUrl = env.REPOSITORY_BASE_URL
const repository = getRepositoryName()
const authStrategy = env.PAGE_BUILDER_AUTH_STRATEGY

const commonRequestArgs = [baseUrl, repository, authStrategy] as const

export function useOnboarding() {
  const { showToast } = useToast()
  const onboarding = useRequest(getOnboarding, [...commonRequestArgs])
  const getOnboardingState = useRefGetter(onboarding)

  async function toggleStep(stepId: string) {
    // no optimistic update here to avoid having a delay between completing the
    // last step and doing some animation.
    try {
      const { completedSteps } = await toggleOnboardingStep(
        stepId,
        baseUrl,
        repository,
        authStrategy
      )
      updateOnboarding({ completedSteps })

      return completedSteps
    } catch (err) {
      showToast({ title: "Failed to toggle onboarding step", icon: "alert" })
      console.error("Failed to toggle onboarding step", err)

      return getOnboardingState().completedSteps
    }
  }

  async function toggleGuide() {
    const previousIsDismissed = getOnboardingState().isDismissed

    try {
      updateOnboarding({ isDismissed: !previousIsDismissed })
      const { isDismissed } = await toggleOnboarding(
        baseUrl,
        repository,
        authStrategy
      )
      updateOnboarding({ isDismissed })
    } catch (err) {
      updateOnboarding({ isDismissed: previousIsDismissed })
      showToast({ title: "Failed to toggle onboarding", icon: "alert" })
      console.error("Failed to toggle onboarding", err)
    }
  }

  function updateOnboarding(updates: Partial<OnboardingState>) {
    updateData(getOnboarding, [...commonRequestArgs], {
      ...getOnboardingState(),
      ...updates
    })
  }

  return { onboarding, toggleStep, toggleGuide }
}
